import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { PlugZap, Wifi, Battery, ShieldCheck, Zap, Sparkles, Home as HomeIcon, ArrowRight, Check, ChevronDown, Star, Calculator, TrendingUp, Timer } from "lucide-react";
import QuoteForm from "../components/QuoteForm";

const HERO_IMG = "/images/batteries/aeg-plug-play.png";

// Simple savings model tuned for AEG Plug & Play 4,8 kWh
// Captures ~ 4-5 kWh/day peak shift, translating to ~€0.15-€0.20 per shifted kWh
const estimateAnnualSavings = (yearlyUsage) => {
  // scale linearly with usage but cap around €780 (physical battery limit)
  const raw = yearlyUsage * 0.16;
  return Math.round(Math.min(raw, 780) / 10) * 10;
};

const estimatePayback = (savings) => {
  if (savings <= 0) return "-";
  const years = 1499 / savings;
  return years.toFixed(1).replace(".", ",");
};

const benefits = [
  { icon: PlugZap, title: "Zelf te installeren", desc: "Geen ingrijpende verbouwing. Werkt via een standaard stopcontact." },
  { icon: Sparkles, title: "Slim energiebeheer", desc: "Laadt op bij goedkope stroom of overschot van je zonnepanelen." },
  { icon: Zap, title: "Werkt met zonnepanelen", desc: "Ook geschikt voor dynamische energiecontracten met uur-tarieven." },
  { icon: Battery, title: "Uitbreidbaar", desc: "Start met 4,8 kWh en breid later eenvoudig uit met extra modules." },
  { icon: HomeIcon, title: "Compact ontwerp", desc: "Past in vrijwel elke bijkeuken, garage of technische ruimte." },
  { icon: ShieldCheck, title: "Tot 10 jaar garantie", desc: "AEG kwaliteitsstandaard. Vertrouwd merk met fabrieksgarantie." },
];

const steps = [
  { n: "1", title: "Plaats de batterij", desc: "Kies een droge plek met een stopcontact in de buurt." },
  { n: "2", title: "Sluit aan op stopcontact", desc: "Standaard 230V. Geen elektricien nodig voor de installatie." },
  { n: "3", title: "Verbind met wifi", desc: "Configuratie via de AEG app in enkele minuten." },
  { n: "4", title: "Koppel de P1-meter", desc: "De batterij leest je slimme meter uit en optimaliseert automatisch." },
];

const specs = [
  ["Opslagcapaciteit", "4,8 kWh"],
  ["Type installatie", "Plug & Play (230V)"],
  ["Koppeling", "AC-gekoppeld"],
  ["App", "Slimme AEG-app"],
  ["Uitbreidbaar", "Ja, met extra modules"],
  ["Compatibiliteit", "Vrijwel alle zonnepanelen"],
  ["Slimme meter", "P1-poort vereist"],
  ["Garantie", "Tot 10 jaar fabrieksgarantie"],
];

const faqs = [
  { q: "Kan ik hem zelf installeren?", a: "Ja. De AEG Plug & Play is ontworpen zodat je hem zelf kunt aansluiten via een standaard stopcontact. Er is geen elektricien of meterkastverbouwing nodig. Je koppelt hem via wifi en de P1-poort van je slimme meter." },
  { q: "Werkt hij zonder zonnepanelen?", a: "Ja. Met een dynamisch energiecontract laadt de batterij automatisch op wanneer stroom goedkoop is (bijvoorbeeld 's nachts) en gebruik je die energie tijdens dure pieken. Zo bespaar je ook zonder zonnepanelen." },
  { q: "Kan ik hem later uitbreiden?", a: "Ja. Je start met 4,8 kWh en kunt later extra modules toevoegen wanneer je energiebehoefte groeit." },
  { q: "Heb ik een slimme meter nodig?", a: "Ja, een slimme meter met open P1-poort is vereist. Vrijwel alle Nederlandse woningen zijn hier inmiddels van voorzien. Onze adviseur helpt je met de check." },
  { q: "Hoe werkt de garantie?", a: "AEG biedt tot 10 jaar fabrieksgarantie op capaciteit en werking. Helios Bright geeft daarnaast persoonlijke service en ondersteuning bij installatie en app-configuratie." },
];

const Faq = ({ q, a, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-b border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((c) => !c)}
        className="w-full flex items-center justify-between py-5 text-left"
        data-testid={`aeg-faq-${i}`}
        aria-expanded={open}
      >
        <span className="font-heading font-bold text-navy-900 text-lg pr-4">{q}</span>
        <ChevronDown size={18} className={`text-brand transition-transform duration-300 flex-shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <p className="pb-5 pr-8 text-slate-600 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AegPlugPlay = () => {
  const [usage, setUsage] = useState(3500);
  const savings = useMemo(() => estimateAnnualSavings(usage), [usage]);
  const payback = useMemo(() => estimatePayback(savings), [savings]);

  React.useEffect(() => {
    const prev = document.title;
    document.title = "AEG Plug & Play Thuisbatterij 4,8 kWh — vanaf € 1.499 | Helios Bright";
    // meta description
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : "";
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "AEG Plug & Play thuisbatterij 4,8 kWh — zelf installeren via standaard stopcontact. Slim energiebeheer, wifi, P1-meter en tot 10 jaar garantie. Vanaf € 1.499. Vraag vrijblijvend een offerte aan bij Helios Bright.");
    return () => {
      document.title = prev;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main className="pt-24" data-testid="aeg-page">
      {/* HERO — premium editorial */}
      <section className="relative overflow-hidden bg-white">
        {/* Background layers */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {/* subtle grid */}
          <div
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a0d 1px, transparent 1px), linear-gradient(90deg, #0f172a0d 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage: "radial-gradient(ellipse at 60% 40%, black 30%, transparent 75%)",
              WebkitMaskImage: "radial-gradient(ellipse at 60% 40%, black 30%, transparent 75%)",
            }}
          />
          {/* soft pink radial */}
          <div className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.22)_0%,rgba(255,47,125,0)_60%)]" />
          <div className="absolute bottom-0 -left-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.10)_0%,rgba(255,47,125,0)_60%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-16 pb-8 lg:pb-14 grid lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          {/* LEFT — copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="lg:col-span-5 relative z-10"
          >
            {/* Live badge row */}
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy-900 text-white text-[11px] font-bold uppercase tracking-[0.18em]">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-brand" />
                </span>
                Nieuw · Plug & Play
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-navy-900 text-xs font-semibold shadow-sm">
                <span className="flex items-center gap-0.5 text-amber-500">
                  {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={11} fill="currentColor" strokeWidth={0} />)}
                </span>
                4,9 · Google reviews
              </span>
            </div>

            <h1 className="mt-6 font-heading text-[2.75rem] leading-[1.02] sm:text-6xl lg:text-[5.5rem] lg:leading-[0.95] font-black tracking-tighter text-navy-900 text-balance">
              De thuisbatterij
              <br />
              die u <span className="gradient-pink-text">zelf</span>
              <br />
              installeert.
            </h1>

            <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-md">
              Sla goedkope stroom of overtollige zonne-energie op en gebruik het wanneer stroom duur is. Aansluiten via een standaard stopcontact — geen elektricien, geen verbouwing.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#offerte" className="btn-pink group" data-testid="aeg-cta-quote">
                Vraag offerte aan
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </a>
              <Link to="/#calculator" className="btn-ghost group" data-testid="aeg-cta-calc">
                <Calculator size={16} className="text-brand" />
                Bereken uw besparing
              </Link>
            </div>

            {/* Trust pills */}
            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-700">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-6 h-6 rounded-full bg-brand-soft text-brand flex items-center justify-center">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="font-semibold">Vanaf € 1.499</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-6 h-6 rounded-full bg-brand-soft text-brand flex items-center justify-center">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="font-semibold">10 jaar garantie</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-6 h-6 rounded-full bg-brand-soft text-brand flex items-center justify-center">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="font-semibold">Geen elektricien</span>
              </span>
            </div>

            {/* Inline mini calculator — instant besparingsindicatie */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.35 }}
              className="mt-8 max-w-md rounded-3xl border border-slate-200/80 bg-white/85 backdrop-blur-md shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] p-5"
              data-testid="aeg-mini-calc"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-brand">
                  <Sparkles size={12} /> Directe besparingsindicatie
                </span>
                <span className="text-[11px] font-semibold text-slate-500">AEG 4,8 kWh</span>
              </div>

              <div className="mt-3 flex items-baseline justify-between gap-4">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Jaarverbruik</div>
                  <div className="font-heading text-2xl font-black text-navy-900 leading-none mt-1">
                    {usage.toLocaleString("nl-NL")} <span className="text-sm font-bold text-slate-500">kWh</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tot</div>
                  <motion.div
                    key={savings}
                    initial={{ scale: 0.9, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.25 }}
                    className="font-heading text-3xl font-black gradient-pink-text leading-none mt-1"
                  >
                    € {savings.toLocaleString("nl-NL")}
                  </motion.div>
                  <div className="text-[11px] font-semibold text-slate-500 mt-0.5">per jaar besparen</div>
                </div>
              </div>

              <input
                type="range"
                min="2000"
                max="6000"
                step="100"
                value={usage}
                onChange={(e) => setUsage(Number(e.target.value))}
                aria-label="Jaarverbruik in kWh"
                data-testid="aeg-mini-calc-slider"
                className="mt-4 w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-200 accent-brand
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white
                  [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-brand
                  [&::-webkit-slider-thumb]:shadow-[0_4px_12px_rgba(255,47,125,0.4)]
                  [&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110
                  [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5
                  [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
                  [&::-moz-range-thumb]:border-4 [&::-moz-range-thumb]:border-brand"
                style={{
                  background: `linear-gradient(to right, #ff2f7d 0%, #ff2f7d ${((usage - 2000) / 4000) * 100}%, #e2e8f0 ${((usage - 2000) / 4000) * 100}%, #e2e8f0 100%)`,
                }}
              />
              <div className="mt-1.5 flex justify-between text-[10px] font-semibold text-slate-400">
                <span>2.000 kWh</span>
                <span>4.000</span>
                <span>6.000 kWh</span>
              </div>

              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5 text-slate-600">
                  <Timer size={13} className="text-brand" />
                  <span>Terugverdientijd ~ <b className="text-navy-900">{payback} jaar</b></span>
                </span>
                <a href="#offerte" className="inline-flex items-center gap-1 text-brand font-bold hover:underline" data-testid="aeg-mini-calc-cta">
                  Ontvang offerte <ArrowRight size={12} />
                </a>
              </div>
              <p className="mt-3 text-[10px] text-slate-400 leading-relaxed">
                Indicatieve besparing o.b.v. dynamisch energiecontract & gemiddeld piek-/dalverschil. Werkelijke besparing hangt af van uw contract en verbruikspatroon.
              </p>
            </motion.div>
          </motion.div>

          {/* RIGHT — product stage */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 relative"
          >
            <div className="relative aspect-[5/4] lg:aspect-[4/3] w-full">
              {/* pink halo behind product */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[70%] h-[70%] rounded-full bg-[radial-gradient(circle,rgba(255,47,125,0.28)_0%,rgba(255,47,125,0)_65%)]" />
              </div>
              {/* rotating conic accent ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="absolute inset-x-[10%] top-[8%] bottom-[8%] rounded-full pointer-events-none opacity-40"
                style={{
                  background:
                    "conic-gradient(from 0deg, rgba(255,47,125,0) 0deg, rgba(255,47,125,0.35) 90deg, rgba(255,47,125,0) 180deg, rgba(255,47,125,0.25) 270deg, rgba(255,47,125,0) 360deg)",
                  filter: "blur(28px)",
                }}
                aria-hidden="true"
              />

              {/* Product image with floating idle animation */}
              <motion.img
                src={HERO_IMG}
                alt="AEG Plug & Play thuisbatterij 4,8 kWh"
                loading="eager"
                fetchpriority="high"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                style={{ mixBlendMode: "multiply" }}
                className="relative z-[1] mx-auto w-[92%] sm:w-[80%] lg:w-[85%] h-full object-contain"
              />

              {/* Breathing LED overlay — pulses over the AEG green LED strip */}
              <motion.div
                aria-hidden="true"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 z-[2] pointer-events-none flex items-start justify-start"
              >
                <div className="relative w-full h-full">
                  {/* Bright core LED line (narrow, over the actual strip) */}
                  <motion.div
                    animate={{ opacity: [0.55, 1, 0.55] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute rounded-full"
                    style={{
                      left: "28%",
                      top: "38.5%",
                      width: "24%",
                      height: "1.5%",
                      background: "linear-gradient(90deg, rgba(34,197,94,0) 0%, #22c55e 20%, #4ade80 50%, #22c55e 80%, rgba(34,197,94,0) 100%)",
                      boxShadow: "0 0 24px 6px rgba(34,197,94,0.9), 0 0 44px 14px rgba(34,197,94,0.55)",
                      mixBlendMode: "screen",
                    }}
                  />
                  {/* Wider soft green halo around the strip */}
                  <motion.div
                    animate={{ opacity: [0.35, 0.7, 0.35], scale: [1, 1.06, 1] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute rounded-full blur-2xl"
                    style={{
                      left: "22%",
                      top: "35.5%",
                      width: "36%",
                      height: "7%",
                      background: "radial-gradient(ellipse, rgba(34,197,94,0.65) 0%, rgba(34,197,94,0) 70%)",
                      mixBlendMode: "screen",
                    }}
                  />
                </div>
              </motion.div>

              {/* Floor reflection line */}
              <div className="absolute left-[8%] right-[8%] bottom-[6%] h-px bg-gradient-to-r from-transparent via-slate-300/50 to-transparent" />

              {/* Floating chip: capacity */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="hidden sm:flex absolute top-[8%] left-[2%] z-20 items-center gap-2 px-3.5 py-2 rounded-2xl bg-white/95 backdrop-blur-md border border-slate-200/80 shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
              >
                <span className="w-8 h-8 rounded-xl gradient-pink text-white flex items-center justify-center">
                  <Battery size={15} />
                </span>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold leading-none">Capaciteit</div>
                  <div className="text-navy-900 font-heading font-black text-sm leading-tight mt-0.5">4,8 kWh</div>
                </div>
              </motion.div>

              {/* Floating chip: garantie */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.65 }}
                className="hidden sm:flex absolute top-[38%] right-[0%] z-20 items-center gap-2 px-3.5 py-2 rounded-2xl bg-white/95 backdrop-blur-md border border-slate-200/80 shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
              >
                <span className="w-8 h-8 rounded-xl bg-navy-900 text-white flex items-center justify-center">
                  <ShieldCheck size={15} />
                </span>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold leading-none">Garantie</div>
                  <div className="text-navy-900 font-heading font-black text-sm leading-tight mt-0.5">Tot 10 jaar</div>
                </div>
              </motion.div>

              {/* Floating chip: wifi/slim */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="hidden sm:flex absolute bottom-[14%] left-[4%] z-20 items-center gap-2 px-3.5 py-2 rounded-full bg-navy-900 text-white shadow-[0_10px_30px_rgba(15,23,42,0.25)]"
              >
                <Wifi size={13} className="text-brand" />
                <span className="text-[11px] font-bold uppercase tracking-wider">Wifi · slim</span>
              </motion.div>

              {/* Floating chip: prijs bubble */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.95 }}
                className="hidden sm:flex absolute bottom-[6%] right-[4%] z-20 flex-col items-center justify-center w-24 h-24 rounded-full gradient-pink text-white shadow-[0_20px_40px_rgba(255,47,125,0.35)] border-4 border-white"
              >
                <span className="text-[10px] uppercase tracking-widest font-bold opacity-90">Vanaf</span>
                <span className="font-heading font-black text-xl leading-none mt-0.5">€ 1.499</span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* USP STRIP — premium glass cards */}
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pb-14 lg:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 rounded-3xl bg-white/70 backdrop-blur-md border border-slate-200/70 p-4 lg:p-6 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]"
          >
            {[
              { icon: PlugZap, title: "Plug & Play", desc: "Sluit aan op een standaard stopcontact." },
              { icon: Sparkles, title: "Slimme app", desc: "Automatisch laden bij goedkope stroom." },
              { icon: Battery, title: "Uitbreidbaar", desc: "Groei mee met extra batterijmodules." },
              { icon: TrendingUp, title: "Meer besparen", desc: "Profiteer van dynamische energietarieven." },
            ].map((u, i) => (
              <div
                key={u.title}
                className="group flex items-start gap-3 p-3 lg:p-4 rounded-2xl hover:bg-brand-soft/30 transition-colors duration-300"
                data-testid={`aeg-usp-${i}`}
              >
                <span className="w-11 h-11 flex-shrink-0 rounded-xl bg-brand-soft text-brand flex items-center justify-center group-hover:gradient-pink group-hover:text-white transition-all duration-300">
                  <u.icon size={19} />
                </span>
                <div>
                  <div className="font-heading font-bold text-navy-900 text-[15px] leading-tight">{u.title}</div>
                  <div className="text-[13px] text-slate-600 mt-1 leading-snug">{u.desc}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Voordelen</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">
              Een thuisbatterij die past bij het moderne huishouden.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.05 }} className="p-6 rounded-2xl bg-slate-50/60 border border-slate-100 hover:bg-white hover:border-brand/20 hover:shadow-[0_20px_50px_rgba(255,47,125,0.08)] hover:-translate-y-1 transition-all duration-300" data-testid={`aeg-benefit-${i}`}>
                <span className="w-11 h-11 rounded-xl gradient-pink text-white flex items-center justify-center mb-4"><b.icon size={20} /></span>
                <h3 className="font-heading font-bold text-lg text-navy-900">{b.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Voor wie? */}
      <section className="section-pad bg-slate-50/70">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Voor wie?</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Ideaal voor het gemiddelde Nederlandse huishouden.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Deze batterij is ontworpen voor huishoudens die zonder complexe installatie willen profiteren van hun zonne-energie of van dynamische stroomtarieven.</p>
          </div>
          <ul className="space-y-3">
            {["2.500 – 4.500 kWh stroomverbruik per jaar", "6 tot 14 zonnepanelen", "Slimme meter met open P1-poort", "Interesse in energiebesparing zonder verbouwing"].map((x) => (
              <li key={x} className="flex items-start gap-3 bg-white rounded-2xl border border-slate-200 p-4">
                <span className="w-7 h-7 rounded-full bg-brand-soft text-brand flex items-center justify-center flex-shrink-0 mt-0.5"><Check size={14} strokeWidth={3} /></span>
                <span className="text-navy-900 font-medium">{x}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Zo werkt het</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Van doos tot draaiende batterij in 4 stappen.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {steps.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }} className="p-6 rounded-2xl border border-slate-200 bg-white relative" data-testid={`aeg-step-${i}`}>
                <div className="font-heading font-black text-5xl gradient-pink-text leading-none">{s.n}</div>
                <h3 className="mt-3 font-heading font-bold text-lg text-navy-900">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-slate-600 mt-8 max-w-2xl mx-auto text-sm leading-relaxed">
            De batterij laadt automatisch wanneer stroom goedkoop is of wanneer er overtollige zonnestroom beschikbaar is, en gebruikt die energie wanneer dat het meest voordelig is.
          </p>
        </div>
      </section>

      {/* Specs */}
      <section className="section-pad bg-slate-50/70">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Specificaties</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Alle technische details.</h2>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <dl>
              {specs.map(([k, v], i) => (
                <div key={k} className={`grid grid-cols-[1fr_1fr] md:grid-cols-[240px_1fr] gap-4 px-6 py-4 ${i % 2 === 1 ? "bg-slate-50/60" : ""} border-b border-slate-100 last:border-0`}>
                  <dt className="text-slate-500 text-sm">{k}</dt>
                  <dd className="text-navy-900 font-semibold">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Prijs + CTA */}
      <section className="section-pad bg-navy-900 text-white relative overflow-hidden">
        <div className="blob-pink absolute -top-20 left-0 opacity-40" />
        <div className="blob-pink absolute -bottom-20 right-0 opacity-30" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Investering</p>
          <div className="font-heading font-black text-2xl text-slate-300">Vanaf</div>
          <div className="font-heading font-black text-7xl md:text-8xl gradient-pink-text mt-1 leading-none">€ 1.499</div>
          <p className="mt-5 text-slate-300 max-w-xl mx-auto leading-relaxed">
            De uiteindelijke prijs is afhankelijk van eventuele accessoires en de gekozen configuratie. Vraag een vrijblijvende offerte aan voor een prijs op maat.
          </p>
          <a href="#offerte" className="btn-pink mt-8" data-testid="aeg-cta-price">Vraag vrijblijvend een offerte aan <ArrowRight size={18} /></a>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Veelgestelde vragen</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Nog vragen?</h2>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 px-6 lg:px-8">
            {faqs.map((f, i) => <Faq key={f.q} {...f} i={i} />)}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="offerte" className="section-pad bg-slate-50/70">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Offerte</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">
              Ontvang jouw AEG Plug & Play offerte binnen 24 uur.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              Onze adviseur kijkt naar je slimme meter, energieverbruik en zonnepaneleninstallatie en stuurt je een offerte op maat.
            </p>
            <ul className="mt-6 space-y-2.5">
              {["Gratis advies", "Geen verplichtingen", "Reactie binnen 24 uur"].map((b) => (
                <li key={b} className="flex items-center gap-3 text-navy-900 font-medium">
                  <span className="w-6 h-6 rounded-full bg-brand-soft text-brand flex items-center justify-center flex-shrink-0"><Check size={13} strokeWidth={3} /></span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <QuoteForm source="aeg_plug_play" calculatorData={{ product: "AEG Plug & Play 4,8 kWh" }} />
        </div>
      </section>
    </main>
  );
};

export default AegPlugPlay;
